/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type { TLAbapFormatRequest, TLAbapFormatResponse, TLHTTPValidationError } from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Format Abap Code
 */
export const formatAbapCode = (
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAbapFormatResponse>(
    {
      url: `/abap/format`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAbapFormatRequest,
      signal,
    },
    options,
  );
};

export const getFormatAbapCodeQueryKey = (tLAbapFormatRequest?: BodyType<TLAbapFormatRequest>) => {
  return ['POST', 'abap', 'format', tLAbapFormatRequest] as const;
};

export const getFormatAbapCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof formatAbapCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof formatAbapCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFormatAbapCodeQueryKey(tLAbapFormatRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof formatAbapCode>>> = ({ signal }) =>
    formatAbapCode(tLAbapFormatRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof formatAbapCode>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FormatAbapCodeQueryResult = NonNullable<Awaited<ReturnType<typeof formatAbapCode>>>;
export type FormatAbapCodeQueryError = ErrorType<TLHTTPValidationError>;

export function useFormatAbapCode<
  TData = Awaited<ReturnType<typeof formatAbapCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof formatAbapCode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof formatAbapCode>>,
          TError,
          Awaited<ReturnType<typeof formatAbapCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFormatAbapCode<
  TData = Awaited<ReturnType<typeof formatAbapCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof formatAbapCode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof formatAbapCode>>,
          TError,
          Awaited<ReturnType<typeof formatAbapCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFormatAbapCode<
  TData = Awaited<ReturnType<typeof formatAbapCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof formatAbapCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Format Abap Code
 */

export function useFormatAbapCode<
  TData = Awaited<ReturnType<typeof formatAbapCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof formatAbapCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFormatAbapCodeQueryOptions(tLAbapFormatRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Format Abap Code
 */
export const prefetchFormatAbapCodeQuery = async <
  TData = Awaited<ReturnType<typeof formatAbapCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof formatAbapCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getFormatAbapCodeQueryOptions(tLAbapFormatRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Format Abap Code
 */
export const invalidateFormatAbapCode = async (
  queryClient: QueryClient,
  tLAbapFormatRequest: BodyType<TLAbapFormatRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getFormatAbapCodeQueryKey(tLAbapFormatRequest) }, options);

  return queryClient;
};
